/**
 * Converters between the ZoomableScheduleCanvas WeeklyAvailability format
 * and the API ManagerAvailability format.
 *
 * Canvas format: WeeklyAvailability — array of 7 DaySchedule objects.
 *   Each DaySchedule has dayOfWeek ("MON"|"TUE"|...) and 48 TimeSlot objects
 *   (index 0–47, status 0=unavailable | 1=available | 2=preferred).
 *
 * API format: Record<ApiDayOfWeek, string[]> where ApiDayOfWeek is
 *   "monday"|"tuesday"|... and the string array contains "HH:mm" strings
 *   for every slot that is available (status 1) OR preferred (status 2).
 */

import type { WeeklyAvailability, DayOfWeek as CanvasDayOfWeek } from "@applicant-reviewer/api/availability";
import { DAYS_OF_WEEK, SLOT_STATUS, SLOTS_PER_DAY } from "@applicant-reviewer/api/availability";
import type { ApiDayOfWeek } from "@applicant-reviewer/api";

// ---------------------------------------------------------------------------
// Day-name mapping helpers
// ---------------------------------------------------------------------------

const CANVAS_TO_API_DAY: Record<CanvasDayOfWeek, ApiDayOfWeek> = {
  MON: "monday",
  TUE: "tuesday",
  WED: "wednesday",
  THU: "thursday",
  FRI: "friday",
  SAT: "saturday",
  SUN: "sunday",
};

const API_TO_CANVAS_DAY: Record<ApiDayOfWeek, CanvasDayOfWeek> = {
  monday: "MON",
  tuesday: "TUE",
  wednesday: "WED",
  thursday: "THU",
  friday: "FRI",
  saturday: "SAT",
  sunday: "SUN",
};

// ---------------------------------------------------------------------------
// Slot index ↔ "HH:mm" string
// ---------------------------------------------------------------------------

/**
 * Convert a 0-based slot index (0 = 00:00, 1 = 00:30, ..., 47 = 23:30)
 * to a "HH:mm" string.
 */
export function slotIndexToHHmm(index: number): string {
  const hour = Math.floor(index / 2);
  const minute = index % 2 === 0 ? "00" : "30";
  return `${String(hour).padStart(2, "0")}:${minute}`;
}

/**
 * Convert a "HH:mm" string to a 0-based slot index.
 * Returns -1 if the string is invalid or doesn't correspond to a 30-min boundary.
 */
export function hhmmToSlotIndex(hhmm: string): number {
  const match = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!match) return -1;
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (hour < 0 || hour > 23 || (minute !== 0 && minute !== 30)) return -1;
  return hour * 2 + (minute === 30 ? 1 : 0);
}

// ---------------------------------------------------------------------------
// canvasToApi
// ---------------------------------------------------------------------------

/**
 * Convert a WeeklyAvailability (canvas format) to the API record format.
 * Only slots with status AVAILABLE (1) or PREFERRED (2) are included in the
 * "HH:mm" array — both statuses mean the manager can interview at that time.
 */
export function canvasToApi(
  weeklyAvailability: WeeklyAvailability
): Record<ApiDayOfWeek, string[]> {
  const result = {} as Record<ApiDayOfWeek, string[]>;

  for (const daySchedule of weeklyAvailability) {
    const apiDay = CANVAS_TO_API_DAY[daySchedule.dayOfWeek];
    result[apiDay] = daySchedule.slots
      .filter((slot) => slot.status === SLOT_STATUS.AVAILABLE || slot.status === SLOT_STATUS.PREFERRED)
      .map((slot) => slotIndexToHHmm(slot.index));
  }

  return result;
}

// ---------------------------------------------------------------------------
// apiToCanvas
// ---------------------------------------------------------------------------

/**
 * Convert an API Record<ApiDayOfWeek, string[]> to a WeeklyAvailability.
 * Slots present in the API array are marked AVAILABLE (1); all others are
 * UNAVAILABLE (0). Manager availability does not distinguish preferred vs
 * available — both are collapsed to AVAILABLE when round-tripping from API.
 */
export function apiToCanvas(
  record: Record<ApiDayOfWeek, string[]>
): WeeklyAvailability {
  return DAYS_OF_WEEK.map((canvasDay) => {
    const apiDay = CANVAS_TO_API_DAY[canvasDay];
    const availableHHmm = new Set(record[apiDay] ?? []);

    const slots = Array.from({ length: SLOTS_PER_DAY }, (_, index) => {
      const hhmm = slotIndexToHHmm(index);
      const status = availableHHmm.has(hhmm) ? SLOT_STATUS.AVAILABLE : SLOT_STATUS.UNAVAILABLE;
      return { index, status } as { index: number; status: 0 | 1 | 2 };
    });

    return { dayOfWeek: canvasDay, slots };
  }) as WeeklyAvailability;
}

/**
 * Build an empty WeeklyAvailability with all slots UNAVAILABLE.
 * Useful as the initial state before API data loads.
 */
export function emptyWeeklyAvailability(): WeeklyAvailability {
  return DAYS_OF_WEEK.map((day) => ({
    dayOfWeek: day,
    slots: Array.from({ length: SLOTS_PER_DAY }, (_, index) => ({
      index,
      status: SLOT_STATUS.UNAVAILABLE as 0 | 1 | 2,
    })),
  })) as WeeklyAvailability;
}
